import { useMemo } from 'react';
import {
    Button,
    ButtonSize,
    ButtonVariant,
    RadioInputRowList,
    Sheet,
    YStack,
} from 'smoothie';

import { transformEnumToDisplay } from '../util/utils';

interface SelectRadioInputSheetProps<TOptions> {
    title: string;
    options: Record<string, TOptions>; // options is the enum passed in as an object
    selectedOption?: TOptions | null;
    onSelectOption: (option: TOptions | null) => void;
    isOpen: boolean;
    onClose: () => void;
    toLabelFn?: (option: TOptions) => string; // a function that converts an enum value to a label
    toDetailsFn?: (option: TOptions) => string | null; // a function that converts an enum value to a details
    includeClearSelectionButton?: boolean;
}

const SelectRadioInputSheet = <TOptions extends string>({
    title,
    options,
    selectedOption,
    isOpen,
    onClose,
    onSelectOption,
    toLabelFn = transformEnumToDisplay,
    toDetailsFn,
    includeClearSelectionButton = false,
}: SelectRadioInputSheetProps<TOptions>) => {
    const items = useMemo(
        () =>
            Object.entries(options).map(([key, value]) => ({
                key,
                label: toLabelFn(value),
                details: toDetailsFn?.(value) ?? undefined,
                isSelected: selectedOption === value,
                onPress: () => {
                    onSelectOption(value);
                    onClose();
                },
            })),
        [options, selectedOption],
    );

    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            showCloseButton
            forceKeyboardCompatibleBottomSheet
        >
            <YStack
                paddingTop={12}
                alignItems="center"
                width="100%"
                $sm={{
                    width: 400,
                    alignSelf: 'center',
                }}
            >
                <RadioInputRowList items={items} />
                {includeClearSelectionButton && (
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.LARGE}
                        text="Remove selection"
                        onPress={() => {
                            onSelectOption(null);
                            onClose();
                        }}
                        marginTop={18}
                    />
                )}
            </YStack>
        </Sheet>
    );
};

export default SelectRadioInputSheet;
